import { useEffect } from "react";
import { Shield, Eye, Lock, Share2, UserCheck, Mail, Scale } from "lucide-react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  // Fix: Ensures the page starts at the header on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="bg-[#fdfcf7] min-h-screen pb-20">
      {/* 
          HERO / HEADER SECTION 
      */}
      <section className="py-20 px-6 border-b-4 border-foreground bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-block px-3 py-1 bg-primary text-white font-black uppercase text-xs tracking-widest mb-6 border-2 border-foreground shadow-brutal-sm">
              Legal Documentation
            </div>
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4">
              Privacy <span className="text-primary italic">Policy.</span>
            </h1>
            <p className="font-bold text-muted-foreground flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" /> Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 
          LEGAL CONTENT 
      */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white border-4 border-foreground p-8 md:p-12 shadow-brutal space-y-16">
          
          {/* Introduction */}
          <div className="prose prose-slate max-w-none">
            <p className="text-xl font-bold leading-relaxed text-foreground">
              DmFlio ("we", "our", "us") respects your privacy. This Privacy Policy explains our protocols for collecting, 
              utilizing, and safeguarding your information when you interact with our automation platform.
            </p>
          </div>

          <div className="grid gap-12">
            {/* Section 1 */}
            <PolicySection 
              icon={<Eye className="w-8 h-8" />}
              title="1. Information Collection"
              content={
                <ul className="space-y-3 font-bold text-muted-foreground">
                  <li className="flex items-center gap-2 border-l-4 border-primary pl-4">Identification: Name and professional email address.</li>
                  <li className="flex items-center gap-2 border-l-4 border-primary pl-4">Platform Data: Instagram account metadata strictly via Official Meta APIs.</li>
                  <li className="flex items-center gap-2 border-l-4 border-primary pl-4">Analytics: Anonymized usage data to optimize system performance.</li>
                </ul>
              }
            />

            {/* Section 2 */}
            <PolicySection 
              icon={<UserCheck className="w-8 h-8" />}
              title="2. Operational Usage"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  We utilize collected data to facilitate core automation services, transmit authorized messages 
                  via Instagram APIs, and ensure the stability of your creator workflows. Data is processed 
                  to provide the specific functionality requested by the user.
                </p>
              }
            />

            {/* Section 3 */}
            <PolicySection 
              icon={<Share2 className="w-8 h-8" />}
              title="3. Data Sharing Protocols"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  DmFlio maintains a strict policy against the sale of user data. Information is only 
                  shared with trusted infrastructure providers like Meta (Instagram API) as required 
                  to execute the platform's automation features.
                </p>
              }
            />

            {/* Section 4 */}
            <PolicySection 
              icon={<Lock className="w-8 h-8" />}
              title="4. Security Infrastructure"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  We implement industry-standard encryption and security protocols to prevent unauthorized 
                  access or data breaches. Our systems are audited regularly to maintain compliance 
                  with modern cybersecurity standards.
                </p>
              }
            />

            {/* Section 5 */}
            <PolicySection 
              icon={<Shield className="w-8 h-8" />}
              title="5. User Data Rights"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  As a user, you retain full ownership of your data. You may request total data deletion, 
                  account termination, or an export of your information at any time by contacting our 
                  compliance team.
                </p>
              }
            />

            {/* Section 6 */}
            <PolicySection 
              icon={<Mail className="w-8 h-8" />}
              title="6. Contact Compliance"
              content={
                <div className="bg-foreground text-white p-6 border-2 border-foreground shadow-brutal-sm">
                  <p className="font-black uppercase tracking-widest text-xs mb-2 text-primary">Inquiries:</p>
                  <p className="text-xl font-bold">hello@dmflio.com</p>
                  <p className="text-xs text-gray-400 mt-2">Subject: Data Privacy Request</p>
                </div>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// Sub-component for individual policy blocks
function PolicySection({ icon, title, content }) {
  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-10 border-t-2 border-muted pt-10">
      <div className="w-16 h-16 bg-white border-2 border-foreground flex items-center justify-center shrink-0 shadow-brutal-sm">
        {icon}
      </div>
      <div className="space-y-4 flex-1">
        <h2 className="text-3xl font-black uppercase tracking-tighter italic">{title}</h2>
        <div className="text-lg">
          {content}
        </div>
      </div>
    </div>
  );
}